import { useRef, useEffect } from 'react';

interface ColorSpectrumProps {
  hue: number;
  saturation: number;
  lightness: number;
  onChange: (saturation: number, lightness: number) => void;
  className?: string;
}

export const ColorSpectrum = ({
  hue,
  saturation,
  lightness,
  onChange,
  className = ''
}: ColorSpectrumProps) => {
  const spectrumRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleColorSelect = (clientX: number, clientY: number) => {
    if (!spectrumRef.current) return;

    const rect = spectrumRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

    const newSaturation = Math.round((x / rect.width) * 100);
    const newLightness = Math.round(100 - (y / rect.height) * 100);

    onChange(newSaturation, newLightness);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDragging.current = true;
    handleColorSelect(e.clientX, e.clientY);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    handleColorSelect(e.clientX, e.clientY);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        handleColorSelect(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div
      ref={spectrumRef}
      className={`relative w-full h-48 rounded-lg cursor-crosshair shadow-inner ${className}`}
      style={{
        background: `
          linear-gradient(to top, #000, transparent),
          linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))
        `
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {/* Curseur de sélection */}
      <div
        className="absolute w-4 h-4 border-2 border-white rounded-full pointer-events-none"
        style={{
          left: `${saturation}%`,
          top: `${100 - lightness}%`,
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)'
        }}
      />
    </div>
  );
};
